import { HierarchyValue } from 'RestAPIs/hierarchy';

export type HierarchyPlanUnit = HierarchyValue & { hierarchy: string[] };

export const createTreeHierarchy = (rows: HierarchyValue[]) => {
  const addHierarchy = (rows: HierarchyValue[]) => {
    const findHierarchy = (
      row: HierarchyValue,
      allRows: HierarchyValue[],
      hierarchy: string[] = []
    ) => {
      // Skip self-referencing rows
      if (row.brDynId === row.parentBrDynId) return hierarchy;

      const parentRow = allRows.find((r) => r.brDynId === row.parentBrDynId);

      if (parentRow) {
        hierarchy.push(parentRow.brId.toString());
        return findHierarchy(parentRow, allRows, hierarchy);
      }

      return hierarchy;
    };

    const rowsWithHierarchy = rows.map((row: HierarchyValue) => {
      const hierarchy = findHierarchy(row, rows);

      return {
        ...row,
        hierarchy: [...hierarchy.reverse(), row.brId.toString()]
      };
    });

    return rowsWithHierarchy;
  };

  return addHierarchy(rows) as HierarchyPlanUnit[];
};

export const getSelectedTreeHierarchy = (
  rows: HierarchyPlanUnit[],
  selectedRowId: string
) => {
  const findDescendants = (
    row: HierarchyPlanUnit,
    allRows: HierarchyPlanUnit[]
  ): HierarchyPlanUnit[] => {
    // Skip self-referencing rows
    if (row.brDynId === row.parentBrDynId) return [];

    const descendants = allRows.filter((r) => r.parentBrDynId === row.brDynId);

    return [
      {
        ...row,
        // The selected row should not have parents in its hierarchy, but only its own id or descendants
        hierarchy:
          selectedRowId === row.brId?.toString()
            ? [row.brId.toString()]
            : row.hierarchy
      },
      ...descendants
        .map((descendant) => ({
          ...descendant,
          // The descendats should only have their own id, the selected row id and the parents in their hierarchy
          hierarchy: descendant.hierarchy.filter(
            (id) =>
              id === descendant.brId.toString() ||
              id === selectedRowId ||
              row.brId.toString() === id
          )
        }))
        .flatMap((descendant) => findDescendants(descendant, allRows))
    ];
  };

  const selectedRow = rows.find((row) => row.brId.toString() === selectedRowId);
  if (!selectedRow) {
    return [];
  }
  return findDescendants(selectedRow, rows);
};

export const isDirectDescendant = (
  parentRow: HierarchyValue,
  childRow: HierarchyPlanUnit
) => {
  return childRow.parentBrDynId === parentRow.brDynId;
};

export const isDescendant = (
  parentRow: HierarchyValue,
  childRow: HierarchyPlanUnit
) => {
  return childRow.hierarchy?.includes(parentRow.brId?.toString());
};
